import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Permissions } from '@app/core/guard/Permissions';
import { VaultBaseComponent } from '@app/shared/components/base/vault-base.component';
import { TableActions } from '@app/shared/components/table/table.component';
import { AppState } from '@app/state';
import { selectIsMobile } from '@app/state/settings/settings.selectors';
import { selectCompanyIds } from '@app/state/auth/auth.selectors';
import {
  Company,
  CompanyCommercialStatus,
  CompanyRegistrationSource,
} from '@interfaces/common/company';
import { Option } from '@interfaces/common/option';
import { Pagination, TablePagination } from '@interfaces/common/pagination';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { CompanyRefreshService } from '@services/helpers/company-refresh.service';
import { PermissionsService } from '@services/helpers/permissions.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { UtilsService } from '@services/helpers/utils.service';
import { CompanyService } from '@services/http/company.service';
import { OnboardingService } from '@services/http/onboarding.service';
import { CnpjFormatter } from '@src/app/core/utils/cnpj.utils';
import { CompanyListContentComponent } from '@src/app/features/dashboard/company/company-list/company-list-content/company-list-content.component';
import { MenuItem } from 'primeng/api';
import { PaginatorState } from 'primeng/paginator';
import {
  Observable,
  Subject,
  catchError,
  distinctUntilChanged,
  map,
  of,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';

// Type-only import for the modal component
type ModalBusinessCaseUploadComponent =
  import('@src/app/features/dashboard/company/company-list/components/company-business-case-upload-modal.component').ModalBusinessCaseUploadComponent;

// Type-only import for the billing parameters dialog component (unified)
type BillingParametersDialogComponent =
  import('@src/app/features/dashboard/management/back-office/pages/billing/components/dialogs/billing-parameters-dialog/billing-parameters-dialog.component').BillingParametersDialogComponent;

// Type-only import for the transfer company modal component
type TransferCompanyModalComponent =
  import('@src/app/features/dashboard/company/company-list/components/modal/transfer-company-modal/transfer-company-modal.component').TransferCompanyModalComponent;

@Component({
  templateUrl: './company-list.component.html',
  styleUrls: ['./company-list.component.scss'],
  standalone: false,
})
export class CompanyListComponent extends VaultBaseComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private loadCompanies$ = new Subject<void>();
  private isResettingPagination = false;
  isMobile: boolean = false;
  breadcrumbs: MenuItem[] = [];
  home: MenuItem = {
    icon: 'pi pi-home',
    routerLink: '/dashboard/dashboard-management',
    label: this.t(this.tk.General.Dashboard),
  };
  paginationRequest: TablePagination = {
    Page: 0,
    PageSize: 10,
  };
  pagination: Pagination = {
    total: 0,
    currentPage: 0,
    pages: 0,
    itemsPerPage: 0,
  };
  search: string = '';
  /**
   * Situação comercial filtrada (card 3072). Vazio lista as duas: esconder prospects por padrão
   * faria o time procurar uma empresa que existe e não aparece.
   */
  commercialStatus: CompanyCommercialStatus | '' = '';
  readonly commercialStatusOptions: Option[] = [
    { label: 'Clientes e prospects', value: '' },
    { label: 'Só clientes', value: 'Cliente' },
    { label: 'Só prospects', value: 'Prospect' },
  ];
  /** Origem do cadastro filtrada (PBI 3576). Vazio lista todas; `CogDataRoom` só as cadastradas pelo Cog. */
  registrationSource: CompanyRegistrationSource | '' = '';
  readonly registrationSourceOptions: Option[] = [
    { label: 'Todas as origens', value: '' },
    { label: 'Cadastradas pelo Cog', value: 'CogDataRoom' },
  ];
  /** Setor padronizado filtrado (card 3642); vazio lista todos. */
  sectorFilter = '';
  /** Empresas marcadas na tabela para a troca de setor em lote (card 3642). */
  selectedCompanies: Company[] = [];
  /** Mostra checkboxes e o menu "Trocar setor" (`COMPANYSECTOR_ASSIGN`). */
  canAssignSector = false;
  companySectorPermissions = Permissions.CompanySector;
  selectedCompanyIds: string[] = [];
  companys: Company[] = [];
  /** A primeira resposta da listagem já chegou (controla esqueleto x vazio). */
  companiesLoaded = false;
  customerPermissions = Permissions.Company;
  economicGroupPermissions = Permissions.EconomicGroup;
  /** Ver a origem do cadastro feito pelo Cog (PBI 3576): mostra o filtro "Origem do cadastro". */
  autoRegistrationPermissions = Permissions.CompanyAutoRegistration;

  constructor(
    translateService: TranslateService,
    store: Store<AppState>,
    private router: Router,
    private utilsService: UtilsService,
    private companyService: CompanyService,
    private toast: ToastHelperService,
    private onboardingService: OnboardingService,
    private companyRefreshService: CompanyRefreshService,
    private permissionsService: PermissionsService
  ) {
    super(translateService, store);
  }

  ngOnInit(): void {
    this.verifyIsMobile();
    this.permissionsService
      .hasPermission(Permissions.CompanySector.ASSIGN)
      .pipe(takeUntil(this.destroy$))
      .subscribe(canAssign => (this.canAssignSector = canAssign));
    this.initCompanyLoadingPipeline();
    this.breadcrumbs = [
      {
        label: this.t(this.tk.Management.Management),
        routerLink: '/',
      },
      {
        label: this.t(this.tk.Management.Companys),
        routerLink: '/dashboard/company/list',
      },
    ];

    this.subscribecompanyIdsFromState();
  }

  formatedCnpj(value: string): string {
    return value ? CnpjFormatter.format(value) : '';
  }

  subscribecompanyIdsFromState(): void {
    if (this.store) {
      this.store
        .select(selectCompanyIds)
        .pipe(
          map(ids => this.normalizeCompanyIds(ids)),
          distinctUntilChanged((prev, curr) => this.areCompanyIdArraysEqual(prev, curr)),
          takeUntil(this.destroy$)
        )
        .subscribe(ids => {
          this.selectedCompanyIds = ids;
          this.resetPagination();
          this.getCompanys();
        });
    }
  }

  getCompanys(): void {
    this.loadCompanies$.next();
  }

  handleTablePagination(pagination: PaginatorState) {
    if (this.isResettingPagination) return;
    this.paginationRequest = {
      Page: !pagination.page ? 1 : pagination.page + 1,
      PageSize: pagination.rows,
    };
    this.getCompanys();
  }

  @ViewChild('list', { static: false })
  companyListContentComponent!: CompanyListContentComponent;

  @ViewChild('businessCaseModal', { static: false })
  businessCaseModal!: ModalBusinessCaseUploadComponent;

  @ViewChild('billingParametersDialog', { static: false })
  billingParametersDialog!: BillingParametersDialogComponent;

  @ViewChild('transferCompanyModal', { static: false })
  transferCompanyModal!: TransferCompanyModalComponent;

  resetPagination(): void {
    this.paginationRequest = {
      ...this.paginationRequest,
      Page: 0,
    };

    if (this.companyListContentComponent) {
      this.isResettingPagination = true;
      this.companyListContentComponent.resetPagination();
      this.isResettingPagination = false;
    }
  }

  handleTableAction(tableActions: TableActions<Company>): void {
    switch (tableActions.action) {
      case 'delete':
        tableActions.items.forEach((user: Company, iCompany: number) => {
          this.utilsService.setIsLoading(true);
          this.removeCompany(user).subscribe({
            next: () => {
              this.utilsService.setIsLoading(false);
              this.toast.showToastSuccess(
                this.t(this.tk.Management.CompanyDeletedSuccessfully),
                ''
              );
              if (iCompany === tableActions.items.length - 1) {
                this.companyRefreshService.triggerRefresh();
                this.companys = [];
                this.getCompanys();
              }
            },
            error: error => {
              this.utilsService.setIsLoading(false);
              console.log('removeUser error: ', error);

              this.toast.showToastError(
                this.t(this.tk.Management.ErrorRemovingCompany),
                error?.errors?.length
                  ? error.errors[0].message
                  : this.t(this.tk.General.RequestError)
              );
            },
          });
        });

        break;
      case 'upload-business-case':
        if (tableActions.items.length > 0) {
          this.businessCaseModal.open(tableActions.items[0].id!);
        }
        break;
      case 'invoicing-parameters':
        if (tableActions.items.length > 0) {
          const company = tableActions.items[0];
          this.billingParametersDialog.companyId = company.id || null;
          this.billingParametersDialog.companyName = company.displayName || null;
          this.billingParametersDialog.level = 'company';
          this.utilsService.setIsLoading(true);
          this.billingParametersDialog.open();
        }
        break;
      case 'transfer-company':
        if (tableActions.items.length > 0) {
          this.transferCompanyModal.open(tableActions.items[0]);
        }
        break;
      default:
        break;
    }
  }

  removeCompany(company: Company): Observable<Company | null> {
    return this.companyService.deleteCompany(company?.id as string);
  }

  addCompany(): void {
    this.router.navigate(['/dashboard/company/register']);
  }

  goToEconomicGroups(): void {
    this.router.navigate(['/dashboard/economic-groups']);
  }

  handleSearch(search: string = ''): void {
    console.log('handleSearch');
    this.search = search;
    this.resetPagination();
    this.getCompanys();
  }

  handleCommercialStatusChange(status: CompanyCommercialStatus | '' | null): void {
    this.commercialStatus = status || '';
    this.resetPagination();
    this.getCompanys();
  }

  handleRegistrationSourceChange(source: CompanyRegistrationSource | '' | null): void {
    this.registrationSource = source || '';
    this.resetPagination();
    this.getCompanys();
  }

  /** Filtro de setor padronizado (card 3642). */
  handleSectorFilterChange(sectorId: string): void {
    this.sectorFilter = sectorId || '';
    this.resetPagination();
    this.getCompanys();
  }

  /** Linhas marcadas na tabela (card 3642). */
  handleCompanySelection(companies: Company[]): void {
    this.selectedCompanies = companies;
  }

  /** Desmarca as linhas da tabela e esconde o menu flutuante. */
  clearCompanySelection(): void {
    this.companyListContentComponent?.clearSelection();
    this.selectedCompanies = [];
  }

  /** Setor gravado em lote: recarrega a página para mostrar o setor novo e desmarca as linhas. */
  onSectorAssigned(): void {
    this.clearCompanySelection();
    this.getCompanys();
  }

  verifyIsMobile() {
    if (this.store) {
      this.store
        .select(selectIsMobile)
        .pipe(takeUntil(this.destroy$))
        .subscribe(isMobile => (this.isMobile = isMobile));
    }
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onBusinessCaseUploadCompleted() {
    // Refresh the company list to reflect any changes
    this.getCompanys();
  }

  onSettingsUpdated() {
    // Refresh the company list to reflect any changes
    this.getCompanys();
  }

  onBillingDialogClosed() {
    this.utilsService.setIsLoading(false);
    this.onSettingsUpdated();
  }

  onBillingDialogLoadingComplete() {
    this.utilsService.setIsLoading(false);
  }

  onTransferCompleted() {
    this.companyRefreshService.triggerRefresh();
    this.getCompanys();
  }

  private initCompanyLoadingPipeline(): void {
    this.loadCompanies$
      .pipe(
        tap(() => this.utilsService.setIsLoading(true)),
        switchMap(() =>
          this.companyService
            .getCompanys(
              this.paginationRequest,
              this.search,
              null,
              this.selectedCompanyIds,
              this.commercialStatus || null,
              this.registrationSource || null,
              this.sectorFilter ? [this.sectorFilter] : null
            )
            .pipe(
              catchError(error => {
                this.utilsService.setIsLoading(false);
                this.toast.showToastError(
                  this.t(this.tk.Management.ErrorLoadingCompanys),
                  error?.errors?.length
                    ? error.errors[0].message
                    : this.t(this.tk.General.RequestError)
                );
                return of(null);
              })
            )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(response => {
        this.utilsService.setIsLoading(false);
        this.companiesLoaded = true;
        if (!response) {
          return;
        }

        this.companys = response?.data || [];

        for (const company of this.companys) {
          company.cnpj = this.formatedCnpj(company.cnpj);
        }

        const { total, currentPage, pages, itensPerPage } = response as unknown as Pagination;
        this.pagination = {
          total,
          currentPage,
          pages,
          itemsPerPage: itensPerPage as number,
        };
      });
  }

  private normalizeCompanyIds(ids: string[] | null | undefined): string[] {
    return [...new Set((ids || []).map(id => String(id)).filter(Boolean))].sort();
  }

  private areCompanyIdArraysEqual(a: string[], b: string[]): boolean {
    if (a.length !== b.length) {
      return false;
    }

    return a.every((id, index) => id === b[index]);
  }
}
